import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Sparkles, MapPin, Clock, CalendarDays, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import LanguageSelector from "@/components/LanguageSelector";
import PropertyCard from "@/components/PropertyCard";

function uid() {
  return "chat_" + Math.random().toString(36).slice(2, 12);
}

export default function Landing() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [sessionId] = useState(uid);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // {role,content}
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => localStorage.setItem("lang", lang), [lang]);

  useEffect(() => {
    api.get("/properties/recent?limit=6").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);

  const started = messages.length > 0;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message, language: lang });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.properties?.length) setProperties(data.properties);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't reach the advisor. Please try again." }]);
    } finally {
      setBusy(false);
      queueMicrotask(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      });
    }
  };

  const suggestions = useMemo(() => {
    const base = {
      en: ["I want to buy an apartment in Istanbul for investment", "A villa in Northern Cyprus by the sea", "A 2-bed flat in Berlin under €400k"],
      tr: ["İstanbul'da yatırım için daire almak istiyorum", "Kuzey Kıbrıs'ta denize sıfır villa", "Berlin'de 400.000 € altı 2+1 daire"],
      de: ["Wohnung in Berlin unter 400.000 € kaufen", "Villa am Meer in Nordzypern", "Investmentwohnung in Istanbul"],
      fr: ["Appartement à Paris sous 700.000 €", "Villa en bord de mer à Chypre du Nord", "Investissement à Istanbul"],
    };
    return base[lang] || base.en;
  }, [lang]);

  return (
    <div className="grain min-h-screen relative">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-30">
        <div className="flex items-center justify-between px-6 sm:px-10 py-5">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <span className="w-6 h-6 grid place-items-center bg-black text-white font-display font-black">Æ</span>
            <span className="font-display text-lg font-medium tracking-tight">Aegis Realtor</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector value={lang} onChange={setLang} />
            <Link
              to="/agent/login"
              data-testid="nav-agent-login"
              className="text-xs tracking-[0.2em] uppercase font-bold hover:underline underline-offset-4"
            >
              {t(lang, "forAgents")}
            </Link>
          </div>
        </div>
      </header>

      {/* Center hero when no messages */}
      <AnimatePresence>
        {!started && (
          <motion.section
            key="hero"
            exit={{ opacity: 0, y: -20 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 relative z-10"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl"
            >
              <motion.img
                data-testid="mascot-hero"
                src="/mascot.png"
                alt="Aegis, your AI real estate advisor"
                initial={{ opacity: 0, y: 20, rotate: -3 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.2, 0.7, 0.2, 1] }}
                className="w-32 sm:w-40 lg:w-48 h-auto mx-auto mb-4 select-none pointer-events-none drop-shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
                style={{ animation: "mascot-float 4s ease-in-out infinite" }}
              />
              <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground mb-6 flex items-center justify-center gap-2">
                <Sparkles className="w-3 h-3" strokeWidth={1.75} /> AI Real Estate Advisor
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black leading-[0.95]">
                {t(lang, "tagline")}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
                {t(lang, "sub")}
              </p>
            </motion.div>

            <motion.form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="mt-10 w-full max-w-2xl glass rounded-2xl p-2 pr-2 flex items-center gap-2"
            >
              <input
                data-testid="chat-input"
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t(lang, "placeholder")}
                className="flex-1 bg-transparent px-4 py-4 text-base sm:text-lg outline-none placeholder:text-muted-foreground"
              />
              <button
                data-testid="chat-submit-btn"
                type="submit"
                disabled={busy || !input.trim()}
                className="h-12 w-12 grid place-items-center rounded-xl bg-black text-white disabled:opacity-30 hover:scale-[1.03] active:scale-95 transition-transform"
              >
                <ArrowUp className="w-5 h-5" strokeWidth={2} />
              </button>
            </motion.form>

            <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-2xl">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`suggest-${i}`}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full hairline hover:bg-black hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Why Arealtor — placed right below hero */}
            <div
              data-testid="why-section"
              className="mt-20 w-full py-14"
              style={{
                background: "var(--paper-2)",
                borderTop: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
                marginLeft: "calc(50% - 50vw)",
                marginRight: "calc(50% - 50vw)",
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-10">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="h-px w-6 bg-[var(--line)]" />
                    <span className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">Aegis Realtor</span>
                    <span className="h-px w-6 bg-[var(--line)]" />
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{t(lang, "whyTitle")}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { icon: Clock, title: t(lang, "why1Title"), text: t(lang, "why1Text"), key: "clock" },
                    { icon: CalendarDays, title: t(lang, "why2Title"), text: t(lang, "why2Text"), key: "calendar" },
                    { icon: Rocket, title: t(lang, "why3Title"), text: t(lang, "why3Text"), key: "rocket" },
                  ].map(({ icon: Icon, title, text, key }, i) => (
                    <div
                      key={key}
                      data-testid={`why-card-${i}`}
                      className="bg-white hairline p-6 text-left flex gap-4 items-start"
                    >
                      <div className="w-11 h-11 grid place-items-center bg-black text-white flex-shrink-0">
                        <Icon className="w-5 h-5" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-display text-lg font-medium leading-tight mb-1">{title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {recent.length > 0 && (
              <div className="mt-20 w-full max-w-6xl px-4">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">{t(lang, "newlyAdded")}</span>
                  <span className="h-px w-6 bg-[var(--line)]" />
                </div>
                <div
                  data-testid="recent-marquee"
                  className="marquee overflow-hidden mask-fade"
                  style={{
                    maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
                    WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
                  }}
                >
                  <div className="marquee-track flex gap-6 items-stretch py-2">
                    {[...recent, ...recent].map((p, i) => (
                      <Link
                        key={`${p.property_id}-${i}`}
                        to={`/property/${p.property_id}`}
                        data-testid={i < recent.length ? `recent-${p.property_id}` : undefined}
                        className="prop-card hairline bg-white flex-shrink-0 w-[280px] sm:w-[320px] block overflow-hidden"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-[var(--paper-2)]">
                          {p.images?.[0] ? (
                            <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">no image</div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase text-muted-foreground mb-1">
                            <MapPin className="w-2.5 h-2.5" strokeWidth={1.75} />
                            <span className="truncate">{p.city}, {p.country}</span>
                          </div>
                          <div className="font-display text-base font-medium truncate">{p.title}</div>
                          <div className="mt-1 font-display text-lg font-semibold tabular-nums">
                            {(() => {
                              try { return new Intl.NumberFormat("en", { style: "currency", currency: p.currency || "EUR", maximumFractionDigits: 0, currencyDisplay: "narrowSymbol" }).format(p.price); }
                              catch { return `${p.currency || "EUR"} ${Number(p.price).toLocaleString()}`; }
                            })()}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* Conversation + results */}
      {started && (
        <main className="pt-24 pb-40 px-6 sm:px-10 max-w-6xl mx-auto relative z-10">
          <div className="max-w-3xl mx-auto mb-6 flex items-center gap-4">
            <img
              data-testid="mascot-inline"
              src="/mascot.png"
              alt="Aegis"
              className="w-16 h-16 rounded-full hairline bg-white object-cover"
            />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Your advisor</div>
              <div className="font-display text-lg font-medium">Aegis</div>
            </div>
          </div>
          <div ref={scrollRef} className="max-w-3xl mx-auto flex flex-col gap-4 mb-10">
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                data-testid={`msg-${m.role}-${i}`}
                className={m.role === "user" ? "self-end max-w-[85%] bg-black text-white px-4 py-3 rounded-2xl rounded-br-sm" : "self-start max-w-[90%] px-4 py-3"}
              >
                <div className={m.role === "assistant" ? "font-display text-lg leading-snug" : "text-sm"}>
                  {m.content}
                </div>
              </motion.div>
            ))}
            {busy && (
              <div className="self-start px-4 py-3 text-sm text-muted-foreground">
                <span className="blink">{t(lang, "thinking")}</span>
              </div>
            )}
          </div>

          {properties.length > 0 && (
            <section data-testid="results-section" className="mt-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                {t(lang, "listings")} · {properties.length}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((p, i) => (
                  <PropertyCard key={p.property_id} p={p} index={i} />
                ))}
              </div>
            </section>
          )}
        </main>
      )}

      {/* Sticky composer when chat started */}
      {started && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-6 sm:px-10 pb-6 pointer-events-none">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="pointer-events-auto max-w-3xl mx-auto glass rounded-2xl p-2 flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t(lang, "placeholder")}
              className="flex-1 bg-transparent px-4 py-3 outline-none placeholder:text-muted-foreground"
            />
            <button
              data-testid="chat-submit-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="h-10 w-10 grid place-items-center rounded-xl bg-black text-white disabled:opacity-30 hover:scale-[1.03] active:scale-95 transition-transform"
            >
              <ArrowUp className="w-4 h-4" strokeWidth={2} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
